import { Injectable } from "@angular/core";
import { DashboardMenuItem } from "../models/dashboard-menu-item";
import { DashboardItemType } from "../models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../golden-layout/golden-layout-addition-mode";
import { BfxBaseMenuService } from "./bfx-base-menu.service";
import {
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
  GoldenLayoutService,
} from "../../golden-layout/golden-layout-service";
import { SystemConfigPromiseService, Roles } from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxAppAdminLauncherService } from "./bfx-app-admin-launcher.service";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class BfxDashboardMenuService extends BfxBaseMenuService {
  private canViewOrderBlotters: boolean;
  private canViewAlgoBlotters: boolean;
  private canViewQuoteBlotters: boolean;
  private canViewTradesBlotters: boolean;
  private canViewInboxBlotter: boolean;
  private canViewDealerInterventionBlotters: boolean;
  private canViewRfqDealerInterventionBlotters: boolean;
  private canViewFindOrders: boolean;
  private canViewFindDeals: boolean;
  private canViewScratchPad: boolean;
  private benchmarksEnabled: boolean;
  private canViewBenchmarkSummary: boolean;
  private canViewOperationsInterventionBlotter: boolean;
  private canViewConnectionMonitor: boolean;
  private algosEnabled: boolean;
  private canViewATDLDesigner: boolean;

  private permissionsInitialized: boolean = false;

  private dialogSubject = new Subject<BfxDialogData>();
  public dialogCtrl$ = this.dialogSubject.asObservable();

  private canViewRiskTradeBlotters: boolean = true;

  constructor(
    private goldenLayoutService: GoldenLayoutService,
    private systemConfigService: SystemConfigPromiseService,
    private settingsLookup: SystemConfigPromiseService,
    private adminLauncherService: BfxAppAdminLauncherService,
    public translationService: TranslateService,
  ) {
    super();
    this.baseGoldenLayout = goldenLayoutService;
    this.initializePermissions();
  }

  private initializePermissions(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (this.permissionsInitialized === true) {
        res(true);
      } else {
        Promise.all([
          this.systemConfigService.canViewOrderBlotters(), //0
          this.systemConfigService.canViewQuoteBlotters(), //1
          this.systemConfigService.canViewTradesBlotters(), //2
          this.systemConfigService.canViewInboxBlotter(), //3
          this.systemConfigService.canViewDealerInterventionBlotters(), //4
          this.systemConfigService.canViewRfqDealerInterventionBlotters(), //5
          this.systemConfigService.canViewFindOrders(), //6
          this.systemConfigService.canViewFindDeals(), //7
          this.systemConfigService.canViewScratchPad(), //8
          this.systemConfigService.areBenchmarksEnabled(), //9
          this.systemConfigService.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_RISK), //10
          this.systemConfigService.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_ORDERS), //11
          this.systemConfigService.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_HEDGE), //12
          this.systemConfigService.canViewOperationsInterventionBlotter(), //13
          this.systemConfigService.canViewConnectionMonitor(), //14
          this.systemConfigService.canEnterAlgoOrder(), //15
          this.systemConfigService.hasRole(Roles.ROLE_ORDER_ALGO_DESIGNER), //16
          this.systemConfigService.canViewAlgoMonitor(),
          this.systemConfigService.canViewRiskTradesBlotters(),
          this.systemConfigService.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_FWD), //19
        ])
          .then(
            (values) => {
              this.canViewOrderBlotters = values[0];
              this.canViewQuoteBlotters = values[1];
              this.canViewTradesBlotters = values[2];
              this.canViewInboxBlotter = values[3];
              this.canViewDealerInterventionBlotters = values[4];
              this.canViewRfqDealerInterventionBlotters = values[5];
              this.canViewFindOrders = values[6];
              this.canViewFindDeals = values[7];
              this.canViewScratchPad = values[8];
              //
              this.benchmarksEnabled = values[9];
              //
              this.canViewBenchmarkSummary = values[10] || values[11] || values[12] || values[19];
              //
              this.canViewOperationsInterventionBlotter = values[13];
              //
              this.canViewConnectionMonitor = values[14];
              this.algosEnabled = values[15];
              this.canViewATDLDesigner = values[16];
              ////
              this.permissionsInitialized = true;
              this.canViewAlgoBlotters = values[17];
              this.canViewRiskTradeBlotters = values[18];
              res(true);
            },
            (rejection) => {
              logger.out.error("ERROR:rejection BfxDashboardMenuService:", rejection);
              this.permissionsInitialized = false;
              res(false);
            },
          )
          .catch((reason) => {
            logger.out.error("ERROR:reason BfxDashboardMenuService: ", reason);
            this.permissionsInitialized = false;
            res(false);
          });
      }
    });
  }

  getMenuProvider(): Promise<Array<DashboardMenuItem>> {
    //mocked
    return new Promise<Array<DashboardMenuItem>>((res, rej) => {
      this.initializePermissions()
        .then(
          (value) => {
            if (value) {
              res(this.constructMenu());
            } else {
              res(null);
            }
          },
          (rejection) => {
            logger.out.error("ERROR:rejection BfxDashboardMenuService:", rejection);
            res(null);
          },
        )
        .catch((reason) => {
          logger.out.error("ERROR:reason BfxDashboardMenuService: ", reason);
          res(null);
        });
    });
  }

  constructMenu(): Array<DashboardMenuItem> {
    let menuItems: DashboardMenuItem[] = [];
    if (this.canViewOrderBlotters) {
      // until we have concept of multiple order blotters, removing the hierarchical view as there is ony 1 item
      // let orders: DashboardMenuItem = this.createMenuItem("Order Blotters", DashboardItemType.Header, GoldenLayoutAdditionMode.DEFAULT);
      // orders.items = [
      menuItems.push(
        this.createMenuItem(
          "Order Blotter",
          DashboardItemType.OrdersBlotter,
          GoldenLayoutAdditionMode.DEFAULT,
        ),
      );
      menuItems.push(
        this.createMenuItem(
          "Order Ladder",
          DashboardItemType.OrdersLadder,
          GoldenLayoutAdditionMode.DEFAULT,
        ),
      );
      // ];
      // menuItems.push(orders);
    }
    if (this.canViewAlgoBlotters) {
      menuItems.push(
        this.createMenuItem(
          "Algo Monitor",
          DashboardItemType.AlgoMonitor,
          GoldenLayoutAdditionMode.DEFAULT,
        ),
      );
    }

    if (this.benchmarksEnabled && this.canViewBenchmarkSummary) {
      menuItems.push(
        this.createMenuItem(
          "Benchmark Summary",
          DashboardItemType.BenchmarkSummary,
          GoldenLayoutAdditionMode.DEFAULT,
          false,
        ),
      );
    }

    menuItems.push(
      this.createMenuItem("", DashboardItemType.Divider, GoldenLayoutAdditionMode.DEFAULT),
    );

    if (this.canViewTradesBlotters || this.canViewRiskTradeBlotters) {
      let trades: DashboardMenuItem = this.createMenuItem(
        "Trade Blotters",
        DashboardItemType.Header,
        GoldenLayoutAdditionMode.DEFAULT,
      );

      trades.items = [
        ...(this.canViewTradesBlotters
          ? [
              this.createMenuItem(
                "Client Trade Blotter",
                DashboardItemType.TradesBlotter,
                GoldenLayoutAdditionMode.DEFAULT,
              ),
            ]
          : []),
        ...(this.canViewRiskTradeBlotters
          ? [
              this.createMenuItem(
                "Risk Trade Blotter",
                DashboardItemType.RiskTradeBlotter,
                GoldenLayoutAdditionMode.DEFAULT,
              ),
            ]
          : []),
      ];

      menuItems.push(trades);
    }

    if (
      this.canViewInboxBlotter ||
      this.canViewDealerInterventionBlotters ||
      this.canViewOperationsInterventionBlotter
    ) {
      let intervention: DashboardMenuItem = this.createMenuItem(
        "Intervention Blotters",
        DashboardItemType.Header,
        GoldenLayoutAdditionMode.DEFAULT,
      );
      intervention.items = [];
      if (this.canViewInboxBlotter) {
        intervention.items.push(
          this.createMenuItem(
            "Inbox",
            DashboardItemType.Inbox,
            GoldenLayoutAdditionMode.DEFAULT,
            false,
          ),
        );
      }
      if (this.canViewDealerInterventionBlotters) {
        intervention.items.push(
          this.createMenuItem(
            "Dealer Intervention",
            DashboardItemType.InterventionBlotter,
            GoldenLayoutAdditionMode.DEFAULT,
            false,
          ),
        );
      }
      if (this.canViewOperationsInterventionBlotter) {
        intervention.items.push(
          this.createMenuItem(
            "Ops Intervention",
            DashboardItemType.OpsIntBlotter,
            GoldenLayoutAdditionMode.DEFAULT,
            false,
          ),
        );
      }
      menuItems.push(intervention);
    }
    if (this.canViewFindOrders || this.canViewFindDeals) {
      let reporting: DashboardMenuItem = this.createMenuItem(
        "Reports",
        DashboardItemType.Header,
        GoldenLayoutAdditionMode.DEFAULT,
      );
      reporting.items = [];
      if (this.canViewFindOrders) {
        reporting.items.push(
          this.createMenuItem(
            "Find Orders",
            DashboardItemType.FindOrders,
            GoldenLayoutAdditionMode.DEFAULT,
            false,
          ),
        );
      }
      if (this.canViewFindDeals) {
        reporting.items.push(
          this.createMenuItem(
            "Find Trades",
            DashboardItemType.FindDeals,
            GoldenLayoutAdditionMode.DEFAULT,
            false,
          ),
        );
      }
      menuItems.push(reporting);
    }
    if (this.canViewScratchPad) {
      menuItems.push(
        this.createMenuItem(
          "Scratch Pad",
          DashboardItemType.ScratchPad,
          GoldenLayoutAdditionMode.DEFAULT,
          false,
        ),
      );
    }

    if (this.canViewConnectionMonitor) {
      menuItems.push(
        this.createMenuItem(
          "Connection Monitor",
          DashboardItemType.ConnectionMonitor,
          GoldenLayoutAdditionMode.DEFAULT,
          false,
        ),
      );
    }

    if (this.algosEnabled && this.canViewATDLDesigner) {
      let config: GLPopoutConfiguration = new GLPopoutConfiguration();
      config.initialState = GLPopoutState.POPOUT;
      config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
      config.preferredSize = { width: 1200, height: 800 };
      config.minimumSize = { width: 800, height: 440 };
      menuItems.push(
        this.createMenuItem(
          "ATDL Designer",
          DashboardItemType.ATDLDesigner,
          GoldenLayoutAdditionMode.DEFAULT,
          false,
          config,
        ),
      );
    }

    if (this.adminLauncherService.canView()) {
      menuItems.push(this.adminLauncherService.getMenuItem());
    }

    return menuItems;
  }
}
